import { useMemo, useState, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { RootState } from 'src/setup/redux/store'
import { toggleSidebar } from 'src/app/modules/ui/redux/uiSlice'
import { NavigationItem } from '../types'
import { createNavigationData } from '../utils/navigationMapper'
import { useLogoutHandler } from '../utils/logoutHandler'
import { useClientBalance } from './useClientBalance'
import { useUnsavedChanges } from 'src/contexts/UnsavedChangesContext'

export const useNewSidebar = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const isMinimized = useSelector((state: RootState) => state.UI.sidebar.isMinimized)
  
  // Modal states for logout flow
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false)
  const [showLogoutConfirmationModal, setShowLogoutConfirmationModal] = useState(false)
  
  // Get unsaved changes state from context
  const { hasUnsavedChanges, clearAllUnsavedChanges } = useUnsavedChanges()
  
  // Logout handler with modal callbacks
  const { logoutHandler, showLogoutConfirmation, executeLogout } = useLogoutHandler({
    hasUnsavedChanges,
    onShowUnsavedChangesModal: () => setShowUnsavedChangesModal(true),
    onShowLogoutConfirmationModal: () => setShowLogoutConfirmationModal(true),
  })
  
  // Fetch real client balance data
  const { balance: clientBalance, loading: balanceLoading, error: balanceError } = useClientBalance()

  // Get notification counts from Redux
  const totalUnreadHandled = useSelector(
    (s: RootState) => s.ChatV2?.myChat?.unreadCount || 0
  )
  const totalUnreadUnhandled = useSelector(
    (s: RootState) => s.ChatV2?.allChat?.unreadCount || 0
  )

  // Create navigation data
  const navigationItems: NavigationItem[] = useMemo(
    () => createNavigationData(totalUnreadHandled, totalUnreadUnhandled, t, logoutHandler),
    [totalUnreadHandled, totalUnreadUnhandled, t, logoutHandler]
  )

  // Use real client balance data with fallback to default values
  const balance = useMemo(() => {
    // If loading or error, provide fallback data to maintain UI consistency
    if (balanceLoading || balanceError || !clientBalance) {
      return {
        amount: 800000,
        creditLimit: 1500000,
        currency: 'Rp' as const,
        paymentType: 'Postpaid' as const
      }
    }
    
    // Use real client balance data
    return clientBalance
  }, [clientBalance, balanceLoading, balanceError])

  // Handle logout
  const handleLogout = () => {
    // Find logout item and trigger its onClick if exists
    const logoutItem = navigationItems.find(item => item.id === 'logout')
    if (logoutItem?.onClick) {
      logoutItem.onClick()
    }
  }

  // Toggle minimize state
  const toggleMinimize = () => {
    dispatch(toggleSidebar())
  }

  // Modal handlers
  const handleUnsavedChangesCancel = useCallback(() => {
    setShowUnsavedChangesModal(false)
  }, [])

  const handleUnsavedChangesDiscard = useCallback(() => {
    clearAllUnsavedChanges()
    setShowUnsavedChangesModal(false)
    // Show logout confirmation after discarding changes
    showLogoutConfirmation()
  }, [clearAllUnsavedChanges, showLogoutConfirmation])

  const handleLogoutConfirmationCancel = useCallback(() => {
    setShowLogoutConfirmationModal(false)
  }, [])

  const handleLogoutConfirmationConfirm = useCallback(() => {
    setShowLogoutConfirmationModal(false)
    executeLogout()
  }, [executeLogout])

  return {
    isMinimized,
    navigationItems,
    balance,
    toggleMinimize,
    handleLogout,
    totalUnreadHandled,
    totalUnreadUnhandled,
    // Modal states and handlers
    showUnsavedChangesModal,
    showLogoutConfirmationModal,
    handleUnsavedChangesCancel,
    handleUnsavedChangesDiscard,
    handleLogoutConfirmationCancel,
    handleLogoutConfirmationConfirm,
  }
}